import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShoppingCart } from "lucide-react";

import { Button, Card, Cargando, ErrorCarga, Input, PageHeader, Select } from "../../components/ui";
import { useToast } from "../../components/Toast";
import { ApiError } from "../../lib/api";
import { listarUsuarios } from "../usuarios/api";
import {
  actualizarConfiguracion,
  verConfiguracion,
  type ConfiguracionComprasRead,
  type ModoCompras,
  type PoliticaSinAprobador,
} from "./api";

/** Estado local del form. Los umbrales son string para el <input number>. */
interface FormState {
  modo: ModoCompras;
  umbral_sic: string;
  umbral_aprobacion: string;
  permite_autoaprobacion: boolean;
  politica_sin_aprobador: PoliticaSinAprobador;
  aprobador_usuario_id: string; // "" = sin aprobador
  tolerancia_oc_pct: string;
}

function desdeConfig(cfg: ConfiguracionComprasRead): FormState {
  return {
    modo: cfg.modo,
    umbral_sic: String(cfg.umbral_sic),
    umbral_aprobacion: String(cfg.umbral_aprobacion),
    permite_autoaprobacion: cfg.permite_autoaprobacion,
    politica_sin_aprobador: cfg.politica_sin_aprobador,
    aprobador_usuario_id:
      cfg.aprobador_usuario_id != null ? String(cfg.aprobador_usuario_id) : "",
    tolerancia_oc_pct: String(cfg.tolerancia_oc_pct),
  };
}

interface Preset {
  label: string;
  aplicar: (prev: FormState) => FormState;
}

const PRESETS: Preset[] = [
  {
    label: "Comité chico",
    aplicar: (prev) => ({
      ...prev,
      modo: "Directo",
      umbral_sic: "0",
      umbral_aprobacion: "0",
      permite_autoaprobacion: true,
      politica_sin_aprobador: "Autoaprobar",
    }),
  },
  {
    label: "Comité mediano",
    aplicar: (prev) => ({
      ...prev,
      modo: "ConSIC",
      umbral_sic: "50000",
      umbral_aprobacion: "200000",
      permite_autoaprobacion: true,
      politica_sin_aprobador: "Autoaprobar",
    }),
  },
  {
    label: "Comité con directiva",
    aplicar: (prev) => ({
      ...prev,
      modo: "ConSIC",
      umbral_sic: "30000",
      umbral_aprobacion: "150000",
      permite_autoaprobacion: false,
      politica_sin_aprobador: "Bloquear",
    }),
  },
];

export function ConfiguracionComprasPage(): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();

  const configQ = useQuery({
    queryKey: ["compras", "configuracion"],
    queryFn: verConfiguracion,
  });
  const usuariosQ = useQuery({
    queryKey: ["usuarios", "activos"],
    queryFn: () => listarUsuarios(true),
    retry: false,
  });

  const [form, setForm] = useState<FormState | null>(null);

  // Inicializa el form desde la query cuando carga.
  useEffect(() => {
    if (configQ.data && form === null) {
      setForm(desdeConfig(configQ.data));
    }
  }, [configQ.data, form]);

  const guardar = useMutation({
    mutationFn: () => {
      if (!form) throw new Error("Formulario no inicializado");
      const aprobador =
        form.aprobador_usuario_id === ""
          ? null
          : Number(form.aprobador_usuario_id);
      return actualizarConfiguracion({
        modo: form.modo,
        umbral_sic: Number(form.umbral_sic),
        umbral_aprobacion: Number(form.umbral_aprobacion),
        permite_autoaprobacion: form.permite_autoaprobacion,
        politica_sin_aprobador: form.politica_sin_aprobador,
        aprobador_usuario_id: aprobador,
        tolerancia_oc_pct: Number(form.tolerancia_oc_pct) || 0,
      });
    },
    onSuccess: (cfg) => {
      toast.success("Política actualizada");
      qc.setQueryData(["compras", "configuracion"], cfg);
      void qc.invalidateQueries({ queryKey: ["compras", "configuracion"] });
    },
    onError: (err) => {
      toast.error(
        err instanceof ApiError ? err.message : "No se pudo guardar la política",
      );
    },
  });

  function set<K extends keyof FormState>(key: K, value: FormState[K]): void {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  const modoDirecto = form?.modo === "Directo";

  return (
    <div className="space-y-4">
      <PageHeader
        title="Política de compras"
        icon={ShoppingCart}
        subtitle={
          <>
            Define cuándo una compra necesita una solicitud interna (SIC) y aprobación. El
            default es &quot;Directo&quot; (sin fricción). A medida que el comité suma personal,
            sube los umbrales y designa un aprobador. Las emergencias nunca se bloquean.
          </>
        }
      />

      {configQ.isPending && <Cargando />}
      {configQ.isError && (
        <ErrorCarga
          error={configQ.error}
          mensaje="Error al cargar la configuración de compras."
          onReintentar={() => configQ.refetch()}
        />
      )}

      {form && (
        <Card padding="md">
          <div className="space-y-5">
            {/* Presets */}
            <div>
              <p className="mb-2 text-sm font-medium text-neutral-700">
                Plantillas rápidas
              </p>
              <div className="flex flex-wrap gap-2">
                {PRESETS.map((p) => (
                  <Button
                    key={p.label}
                    variant="secondary"
                    size="sm"
                    onClick={() => setForm((prev) => (prev ? p.aplicar(prev) : prev))}
                    disabled={guardar.isPending}
                  >
                    {p.label}
                  </Button>
                ))}
              </div>
              <p className="mt-1 text-xs text-neutral-500">
                Rellenan el formulario con valores sugeridos. No se guardan hasta
                que confirmes.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Select
                label="Modo de compras"
                value={form.modo}
                onChange={(e) => set("modo", e.target.value as ModoCompras)}
                disabled={guardar.isPending}
              >
                <option value="Directo">Directo (sin fricción)</option>
                <option value="ConSIC">Con SIC (solicitud interna)</option>
              </Select>

              <Input
                label="Umbral SIC $ — sobre este monto se exige una solicitud"
                type="number"
                min={0}
                value={form.umbral_sic}
                onChange={(e) => set("umbral_sic", e.target.value)}
                disabled={guardar.isPending || modoDirecto}
              />

              <Input
                label="Umbral de aprobación $ — sobre este monto se exige un aprobador distinto"
                type="number"
                min={0}
                value={form.umbral_aprobacion}
                onChange={(e) => set("umbral_aprobacion", e.target.value)}
                disabled={guardar.isPending}
              />

              <Select
                label="Si falta aprobador designado"
                value={form.politica_sin_aprobador}
                onChange={(e) =>
                  set(
                    "politica_sin_aprobador",
                    e.target.value as PoliticaSinAprobador,
                  )
                }
                disabled={guardar.isPending}
              >
                <option value="Autoaprobar">Autoaprobar</option>
                <option value="Bloquear">Bloquear</option>
              </Select>
            </div>

            <p className="text-xs text-neutral-500">
              Autoaprobar deja pasar con ratificación pendiente; Bloquear detiene
              hasta designar uno (excepto emergencias).
            </p>

            <Input
              label="Tolerancia de la OC sobre la SIC aprobada (%)"
              type="number"
              min={0}
              max={100}
              value={form.tolerancia_oc_pct}
              onChange={(e) => set("tolerancia_oc_pct", e.target.value)}
              disabled={guardar.isPending}
              containerClassName="md:max-w-xs"
            />
            <p className="text-xs text-neutral-500">
              Una orden de compra no puede superar el monto aprobado en su solicitud más
              este porcentaje. Con 0 la OC tiene que calzar exacto.
            </p>

            <Select
              label="Aprobador designado"
              value={form.aprobador_usuario_id}
              onChange={(e) => set("aprobador_usuario_id", e.target.value)}
              disabled={guardar.isPending}
              containerClassName="md:max-w-md"
            >
              <option value="">— Sin aprobador —</option>
              {usuariosQ.data?.map((u) => (
                <option key={u.id} value={String(u.id)}>
                  {u.nombre}
                  {u.cargo ? ` · ${u.cargo}` : ""}
                </option>
              ))}
            </Select>

            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={form.permite_autoaprobacion}
                onChange={(e) =>
                  set("permite_autoaprobacion", e.target.checked)
                }
                disabled={guardar.isPending}
                className="mt-0.5 h-4 w-4 rounded border-neutral-450"
              />
              <span className="text-sm text-neutral-700">
                Permitir que el solicitante apruebe su propia SIC (comité
                unipersonal)
              </span>
            </label>

            <div className="flex justify-end border-t border-neutral-100 pt-4">
              <Button
                onClick={() => guardar.mutate()}
                disabled={guardar.isPending}
              >
                {guardar.isPending ? "Guardando…" : "Guardar"}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
